/**
 * Ce que la lecture automatique a vraiment fait gagner — mesuré sur chaque
 * feuille réelle, à l'inscription (plan OCR du 07/10/2026, chantier F).
 *
 * Un banc d'essai dit ce qu'un moteur sait lire ; seule la production dit si
 * l'agent a gagné du temps. On compare, champ par champ, ce que la lecture a
 * proposé à ce que l'agent a inscrit. Que des nombres : aucune valeur lue ne
 * quitte la feuille.
 */
import { z } from 'zod';
/** Les champs que la lecture remplit à partir de l'écriture à la main. */
export const CHAMPS_BILAN_LECTURE = ['heure', 'nom', 'prestation'];
const schemaCompte = z.object({
    /** Proposée, et inscrite telle quelle. */
    justes: z.number().int().nonnegative(),
    /** Proposée, puis corrigée par l'agent. */
    corrigees: z.number().int().nonnegative(),
    /** Rien de proposé : l'agent a tout saisi. */
    completees: z.number().int().nonnegative(),
});
export const schemaBilanLecture = z.object({
    heure: schemaCompte,
    nom: schemaCompte,
    prestation: schemaCompte,
    /** Lignes du papier que la lecture n'avait pas vues, ajoutées par l'agent. */
    lignesAjoutees: z.number().int().nonnegative(),
});
/** Le même texte, à la casse et aux espaces près : une majuscule n'est pas une correction. */
const pareil = (a, b) => a.trim().replace(/\s+/gu, ' ').toUpperCase() === b.trim().replace(/\s+/gu, ' ').toUpperCase();
/**
 * Le bilan des lignes inscrites. Les lignes écartées n'y entrent pas : elles
 * ne disent rien de la lecture, mais de la feuille.
 */
export function bilanDeLecture(proposees, inscrites) {
    const parLigne = new Map(proposees.map((p) => [p.numeroLigne, p]));
    const vide = () => ({
        justes: 0,
        corrigees: 0,
        completees: 0,
    });
    const bilan = { heure: vide(), nom: vide(), prestation: vide(), lignesAjoutees: 0 };
    for (const ligne of inscrites) {
        const proposee = parLigne.get(ligne.numeroLigne);
        if (proposee === undefined) {
            bilan.lignesAjoutees += 1;
            continue;
        }
        for (const champ of CHAMPS_BILAN_LECTURE) {
            const lu = proposee[champ];
            if (lu.trim() === '')
                bilan[champ].completees += 1;
            else if (pareil(lu, ligne[champ]))
                bilan[champ].justes += 1;
            else
                bilan[champ].corrigees += 1;
        }
    }
    return bilan;
}
/** Le bilan compte-t-il quelque chose ? Rien d'inscrit, rien à dire. */
export function bilanLectureVide(bilan) {
    return (bilan.lignesAjoutees === 0 &&
        CHAMPS_BILAN_LECTURE.every((c) => bilan[c].justes + bilan[c].corrigees + bilan[c].completees === 0));
}
/** « heures 4 justes sur 5 · noms 3 sur 5 · prestations 2 sur 5 » — pour l'écran. */
export function resumeBilanLecture(bilan) {
    const LIBELLES = {
        heure: 'heures',
        nom: 'noms',
        prestation: 'prestations',
    };
    const parties = CHAMPS_BILAN_LECTURE.map((champ) => {
        const c = bilan[champ];
        const total = c.justes + c.corrigees + c.completees;
        return `${LIBELLES[champ]} ${String(c.justes)} juste${c.justes > 1 ? 's' : ''} sur ${String(total)}`;
    });
    if (bilan.lignesAjoutees > 0) {
        parties.push(`${String(bilan.lignesAjoutees)} ligne${bilan.lignesAjoutees > 1 ? 's' : ''} ajoutée${bilan.lignesAjoutees > 1 ? 's' : ''} à la main`);
    }
    return parties.join(' · ');
}
//# sourceMappingURL=bilan-lecture.js.map